'use client';

import type { ElementType } from 'react';

import React, { forwardRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

interface ParagraphProps {
  children?: React.ReactNode;
  innerHtml?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  tag?: ElementType;
  color?: 'white' | 'muted-foreground' | 'muted' | 'black' | 'primary' | 'foreground' | 'neutral';
  onClick?: () => void;
}

export const Paragraph = forwardRef<HTMLDivElement, ParagraphProps>(
  (
    { children, innerHtml, className, size = 'lg', color = 'black', tag: Tag = 'div', onClick },
    ref,
  ) => {
    if (innerHtml) {
      return (
        <Tag
          ref={ref}
          onClick={onClick}
          className={cn(
            'tracking-tight transition-all',
            'ea-paragraph__inner-html',
            `ea-paragraph__inner-${size}`,
            `ea-paragraph__inner-${color}`,
            onClick && 'cursor-pointer text-opacity-80',
            className,
          )}
          dangerouslySetInnerHTML={{
            // Legacy imported review smiley has an equivalent verified HTTPS asset.
            __html: innerHtml.replaceAll(
              'http://www.oknaprymat.sk/admin/editor/plugins/smiley/images/wink_smile.gif',
              'https://www.oknaprymat.sk/admin/editor/plugins/smiley/images/wink_smile.gif',
            ),
          }}
        />
      );
    }

    return (
      <Tag
        ref={ref}
        onClick={onClick}
        className={cn(
          'ea-paragraph',
          'font-light transition-all',
          size === 'xs' && 'text-[10px] leading-[130%] sm:text-[12px]',
          size === 'sm' && 'text-xs sm:text-sm',
          size === 'base' && 'text-sm sm:text-base',
          size === 'lg' && 'text-base leading-[130%] sm:text-lg sm:leading-[130%]',
          size === 'xl' && 'text-lg lg:text-xl',
          size === '2xl' && 'text-lg sm:text-xl lg:text-2xl',
          color === 'white' && 'text-white',
          color === 'muted-foreground' && 'text-[#666666]',
          color === 'neutral' && 'text-[#6B7280]',
          color === 'black' && 'text-black',
          color === 'muted' && 'text-muted',
          color === 'foreground' && 'text-foreground',
          color === 'primary' && 'text-primary',
          onClick && 'cursor-pointer text-opacity-80',
          className,
        )}
      >
        {children}
      </Tag>
    );
  },
);

Paragraph.displayName = 'Paragraph';
